"use client";

import { useState } from "react";
import Link from "next/link";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const PRICE = 89.99;

interface CartItem {
  id: string;
  name: string;
  variant: string;
  descriptor: string;
  color: string;
  price: number;
  quantity: number;
}

const initialCartItems: CartItem[] = [
  {
    id: "green",
    name: "Kratapillar Premium Kratom Powder",
    variant: "Green Line",
    descriptor: "Balanced",
    color: "#3F5A45",
    price: PRICE,
    quantity: 1,
  },
];

export default function CartPage() {
  const [cartItems, setCartItems] = useState<CartItem[]>(initialCartItems);

  const updateQuantity = (id: string, delta: number) => {
    setCartItems((prev) =>
      prev
        .map((item) =>
          item.id === id ? { ...item, quantity: Math.max(0, item.quantity + delta) } : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const removeItem = (id: string) => {
    setCartItems((prev) => prev.filter((item) => item.id !== id));
  };

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const shipping = 0;
  const total = subtotal + shipping;

  return (
    <div style={{ backgroundColor: "#F6F5F2" }}>
      <Header />
      <main className="min-h-screen">
        {/* Page Header */}
        <div
          className="py-12 border-b"
          style={{ borderColor: "#E0DFDB", backgroundColor: "#F6F5F2" }}
        >
          <div className="max-w-container mx-auto px-6 lg:px-8">
            <span className="section-label mb-3 block">Your Order</span>
            <h1
              className="font-serif font-bold"
              style={{
                fontFamily: "Fraunces, Georgia, serif",
                fontSize: "clamp(2rem, 4vw, 3rem)",
                letterSpacing: "-0.03em",
                lineHeight: "1.0",
                color: "#1F1F1F",
              }}
            >
              Shopping Cart
            </h1>
          </div>
        </div>

        <div className="max-w-container mx-auto px-6 lg:px-8 py-12">
          {cartItems.length === 0 ? (
            /* Empty cart state */
            <div className="text-center py-24">
              <div className="mb-6">
                <svg
                  width="64"
                  height="64"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="mx-auto"
                  style={{ opacity: 0.25 }}
                >
                  <path
                    d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"
                    stroke="#1F1F1F"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M3 6h18M16 10a4 4 0 01-8 0"
                    stroke="#1F1F1F"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2
                className="font-serif font-semibold mb-3"
                style={{
                  fontFamily: "Fraunces, Georgia, serif",
                  fontSize: "1.5rem",
                  color: "#1F1F1F",
                  letterSpacing: "-0.02em",
                }}
              >
                Your cart is empty
              </h2>
              <p
                className="text-sm mb-8"
                style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
              >
                Explore our premium kratom lines and find your fit.
              </p>
              <Link href="/shop" className="btn-primary">
                Shop Now
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-16">
              {/* Cart Items */}
              <div className="lg:col-span-2">
                <div className="space-y-4">
                  {cartItems.map((item) => (
                    <div
                      key={item.id}
                      className="p-6"
                      style={{
                        backgroundColor: "white",
                        border: "1px solid #E0DFDB",
                      }}
                    >
                      <div className="flex gap-5">
                        {/* Product color swatch / image placeholder */}
                        <div
                          className="w-20 h-20 flex-shrink-0 flex items-center justify-center"
                          style={{
                            backgroundColor: `${item.color}12`,
                            border: `1.5px solid ${item.color}40`,
                          }}
                        >
                          <div
                            className="w-6 h-6 rounded-full"
                            style={{ backgroundColor: item.color }}
                          />
                        </div>

                        {/* Item details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <h3
                                className="font-semibold text-sm mb-1"
                                style={{
                                  color: "#1F1F1F",
                                  fontFamily: "Inter, system-ui, sans-serif",
                                }}
                              >
                                {item.name}
                              </h3>
                              <div className="flex items-center gap-2 mb-1">
                                <div
                                  className="w-2 h-2 rounded-full"
                                  style={{ backgroundColor: item.color }}
                                />
                                <span
                                  className="text-xs"
                                  style={{
                                    color: "#6B6B6B",
                                    fontFamily: "Inter, system-ui, sans-serif",
                                  }}
                                >
                                  {item.variant} — {item.descriptor}
                                </span>
                              </div>
                              <p
                                className="text-xs"
                                style={{
                                  color: "#6B6B6B",
                                  fontFamily: "Inter, system-ui, sans-serif",
                                }}
                              >
                                1 Kilo · Lab Tested · Free Sample Included
                              </p>
                            </div>
                            <div
                              className="font-serif font-bold text-lg flex-shrink-0"
                              style={{
                                fontFamily: "Fraunces, Georgia, serif",
                                color: "#1F1F1F",
                                letterSpacing: "-0.02em",
                              }}
                            >
                              ${(item.price * item.quantity).toFixed(2)}
                            </div>
                          </div>

                          {/* Quantity + Remove */}
                          <div className="flex items-center justify-between mt-4">
                            <div
                              className="flex items-center"
                              style={{ border: "1px solid #E0DFDB" }}
                            >
                              <button
                                onClick={() => updateQuantity(item.id, -1)}
                                className="w-9 h-9 flex items-center justify-center transition-colors hover:bg-gray-50"
                                style={{ color: "#1F1F1F" }}
                                aria-label="Decrease quantity"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                  <path
                                    d="M5 12h14"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              </button>
                              <span
                                className="w-10 text-center text-sm font-semibold"
                                style={{
                                  color: "#1F1F1F",
                                  fontFamily: "Inter, system-ui, sans-serif",
                                  borderLeft: "1px solid #E0DFDB",
                                  borderRight: "1px solid #E0DFDB",
                                  lineHeight: "2.25rem",
                                }}
                              >
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => updateQuantity(item.id, 1)}
                                className="w-9 h-9 flex items-center justify-center transition-colors hover:bg-gray-50"
                                style={{ color: "#1F1F1F" }}
                                aria-label="Increase quantity"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                  <path
                                    d="M12 5v14M5 12h14"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              </button>
                            </div>
                            <button
                              onClick={() => removeItem(item.id)}
                              className="text-xs transition-colors"
                              style={{
                                color: "#6B6B6B",
                                fontFamily: "Inter, system-ui, sans-serif",
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.color = "#1F1F1F")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.color = "#6B6B6B")
                              }
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Continue shopping */}
                <div className="mt-6">
                  <Link
                    href="/shop"
                    className="inline-flex items-center gap-2 text-sm transition-colors"
                    style={{
                      color: "#6B6B6B",
                      fontFamily: "Inter, system-ui, sans-serif",
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M19 12H5M12 5l-7 7 7 7"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Continue Shopping
                  </Link>
                </div>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div
                  className="p-6 sticky top-24"
                  style={{ backgroundColor: "white", border: "1px solid #E0DFDB" }}
                >
                  <h2
                    className="font-serif font-semibold mb-6"
                    style={{
                      fontFamily: "Fraunces, Georgia, serif",
                      fontSize: "1.25rem",
                      color: "#1F1F1F",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Order Summary
                  </h2>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center justify-between">
                      <span
                        className="text-sm"
                        style={{
                          color: "#6B6B6B",
                          fontFamily: "Inter, system-ui, sans-serif",
                        }}
                      >
                        Subtotal (
                        {cartItems.reduce((s, i) => s + i.quantity, 0)} item
                        {cartItems.reduce((s, i) => s + i.quantity, 0) !== 1
                          ? "s" :""}
                        )
                      </span>
                      <span
                        className="text-sm font-semibold"
                        style={{
                          color: "#1F1F1F",
                          fontFamily: "Inter, system-ui, sans-serif",
                        }}
                      >
                        ${subtotal.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span
                        className="text-sm"
                        style={{
                          color: "#6B6B6B",
                          fontFamily: "Inter, system-ui, sans-serif",
                        }}
                      >
                        Shipping
                      </span>
                      <span
                        className="text-sm font-semibold"
                        style={{
                          color: "#3F5A45",
                          fontFamily: "Inter, system-ui, sans-serif",
                        }}
                      >
                        Free
                      </span>
                    </div>
                    <div
                      className="pt-3 mt-3 border-t flex items-center justify-between"
                      style={{ borderColor: "#E0DFDB" }}
                    >
                      <span
                        className="font-semibold"
                        style={{
                          color: "#1F1F1F",
                          fontFamily: "Inter, system-ui, sans-serif",
                        }}
                      >
                        Total
                      </span>
                      <span
                        className="font-serif font-bold text-2xl"
                        style={{
                          fontFamily: "Fraunces, Georgia, serif",
                          color: "#1F1F1F",
                          letterSpacing: "-0.03em",
                        }}
                      >
                        ${total.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <Link href="/checkout" className="btn-primary w-full justify-center text-sm">
                    Proceed to Checkout
                  </Link>

                  <div className="mt-4 space-y-2">
                    {[
                      "Free shipping on all orders",
                      "30-day money-back guarantee",
                      "Secure checkout",
                    ].map((trust) => (
                      <div key={trust} className="flex items-center gap-2">
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          style={{ flexShrink: 0 }}
                        >
                          <path
                            d="M20 6L9 17l-5-5"
                            stroke="#3F5A45"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span
                          className="text-xs"
                          style={{
                            color: "#6B6B6B",
                            fontFamily: "Inter, system-ui, sans-serif",
                          }}
                        >
                          {trust}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
